import React from "react";
import api, { apiError } from "@/lib/api";
import { PageHeader, Card, SkeletonRows } from "@/components/dash/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Archive, ArchiveRestore, History, Eye, ShieldCheck, FileText, ChevronDown, ChevronUp } from "lucide-react";

const TYPES = [
  { key: "client", label: "Client" },
  { key: "vendor", label: "Vendor" },
  { key: "staffing", label: "Staffing Company" },
];
const SECTIONS = ["Company Information", "Contact Information", "Authorized Representative",
  "CEO / Leadership", "Business Details", "Services & Capabilities", "Compliance", "Other"];

const inputCls = "mt-1 w-full rounded-lg border border-slate-200 px-3 py-2 text-sm text-slate-800 focus:border-vs-royal outline-none";

function Toggle({ checked, onChange, testid }) {
  return (
    <button type="button" onClick={() => onChange(!checked)} data-testid={testid}
      className={`relative h-5 w-9 rounded-full transition-colors ${checked ? "bg-vs-royal" : "bg-slate-200"}`}>
      <span className={`absolute top-0.5 h-4 w-4 rounded-full bg-white shadow transition-transform ${checked ? "translate-x-4" : "translate-x-0.5"}`} />
    </button>
  );
}

function ItemDialog({ open, onClose, onSave, item, kind, saving }) {
  const isDoc = kind === "document";
  const [f, setF] = React.useState({});
  React.useEffect(() => {
    if (open) setF(item ? { ...item, conditional_field: item.conditional_on?.field || "" } : {
      key: "", label: "", section: "Other", required: false, active: true, help_text: "",
      display_order: 999, verification_required: false, conditional_field: "",
      ...(isDoc ? { description: "", expiry_required: false, allowed_file_types: ["pdf", "jpg", "jpeg", "png"], max_file_size_mb: 10 } : {}),
    });
  }, [open, item, isDoc]);
  const set = (k) => (e) => setF({ ...f, [k]: e.target ? e.target.value : e });
  const save = () => {
    if (!f.label?.trim() || (!item && !f.key?.trim())) { toast.error("Label and key are required"); return; }
    const payload = { ...f };
    payload.conditional_on = f.conditional_field ? { field: f.conditional_field, equals: true } : null;
    delete payload.conditional_field;
    if (isDoc && typeof payload.allowed_file_types === "string")
      payload.allowed_file_types = payload.allowed_file_types.split(",").map((s) => s.trim()).filter(Boolean);
    if (!isDoc && Array.isArray(f.source) === false && item?.source) payload.source = item.source;
    onSave(payload);
  };
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{item ? "Edit" : "Add"} {isDoc ? "Document Requirement" : "Profile Field"}</DialogTitle>
          <DialogDescription>Configure how this requirement applies to the partner type. Changes are versioned and audited.</DialogDescription></DialogHeader>
        <div className="space-y-3 mt-2">
          {!item && <div><Label>Field Key (stable identifier)</Label>
            <Input value={f.key || ""} onChange={set("key")} placeholder="e.g. pan_number" data-testid="tmpl-item-key" className="mt-1" /></div>}
          <div><Label>Display Label</Label><Input value={f.label || ""} onChange={set("label")} data-testid="tmpl-item-label" className="mt-1" /></div>
          {!isDoc && <div><Label>Section</Label>
            <select value={f.section || "Other"} onChange={set("section")} className={inputCls} data-testid="tmpl-item-section">
              {SECTIONS.map((s) => <option key={s}>{s}</option>)}
            </select></div>}
          {isDoc && <div><Label>Description</Label><Input value={f.description || ""} onChange={set("description")} data-testid="tmpl-item-desc" className="mt-1" /></div>}
          <div><Label>Help Text</Label><Input value={f.help_text || ""} onChange={set("help_text")} data-testid="tmpl-item-help" className="mt-1" /></div>
          <div><Label>Conditional Requirement (field key, e.g. gst_registered — empty = always applies)</Label>
            <Input value={f.conditional_field || ""} onChange={set("conditional_field")} placeholder="gst_registered" data-testid="tmpl-item-cond" className="mt-1" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Display Order</Label><Input type="number" value={f.display_order ?? 999} onChange={set("display_order")} data-testid="tmpl-item-order" className="mt-1" /></div>
            {isDoc && <div><Label>Max File Size (MB)</Label><Input type="number" value={f.max_file_size_mb ?? 10} onChange={set("max_file_size_mb")} data-testid="tmpl-item-maxsize" className="mt-1" /></div>}
          </div>
          {isDoc && <div><Label>Allowed File Types (comma separated)</Label>
            <Input value={Array.isArray(f.allowed_file_types) ? f.allowed_file_types.join(", ") : f.allowed_file_types || ""} onChange={set("allowed_file_types")} data-testid="tmpl-item-filetypes" className="mt-1" /></div>}
          <div className="flex flex-wrap gap-6 pt-1">
            <label className="flex items-center gap-2 text-sm text-slate-700"><Toggle checked={!!f.required} onChange={(v) => setF({ ...f, required: v })} testid="tmpl-item-required" />Required</label>
            <label className="flex items-center gap-2 text-sm text-slate-700"><Toggle checked={!!f.verification_required} onChange={(v) => setF({ ...f, verification_required: v })} testid="tmpl-item-verif" />Verification Required</label>
            {isDoc && <label className="flex items-center gap-2 text-sm text-slate-700"><Toggle checked={!!f.expiry_required} onChange={(v) => setF({ ...f, expiry_required: v })} testid="tmpl-item-expiry" />Expiry Tracked</label>}
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} data-testid="tmpl-item-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving} className="bg-vs-royal text-white" data-testid="tmpl-item-save">{saving ? "Saving…" : "Save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function ItemsTable({ items, kind, onEdit, onArchive, onRestore, ptype }) {
  const isDoc = kind === "document";
  return (
    <div className="overflow-x-auto min-w-0 -mx-5 px-5">
      <table className="w-full min-w-[760px] text-sm" data-testid={`tmpl-table-${kind}`}>
        <thead><tr className="text-left text-xs uppercase tracking-wider text-slate-400 border-b border-slate-100">
          <th className="py-2.5 font-semibold">{isDoc ? "Document" : "Field"}</th>
          {!isDoc && <th className="py-2.5 font-semibold">Section</th>}
          <th className="py-2.5 font-semibold">Key</th>
          <th className="py-2.5 font-semibold">Requirement</th>
          <th className="py-2.5 font-semibold">Verification</th>
          <th className="py-2.5 font-semibold">Status</th>
          <th className="py-2.5 font-semibold text-right">Actions</th>
        </tr></thead>
        <tbody>
          {items.map((it) => (
            <tr key={it.key} className={`border-b border-slate-50 ${!it.active ? "opacity-50" : ""}`} data-testid={`tmpl-row-${it.key}`}>
              <td className="py-3">
                <div className="font-medium text-slate-800">{it.label}</div>
                {it.help_text ? <div className="text-xs text-slate-400">{it.help_text}</div> : null}
              </td>
              {!isDoc && <td className="py-3 text-slate-600">{it.section}</td>}
              <td className="py-3"><code className="text-xs bg-slate-50 px-1.5 py-0.5 rounded text-slate-500">{it.key}</code></td>
              <td className="py-3">
                {it.required
                  ? <span className="text-xs font-semibold text-rose-600">Required{it.conditional_on ? "*" : ""}</span>
                  : <span className="text-xs font-semibold text-slate-400">Optional{it.conditional_on ? "*" : ""}</span>}
                {it.conditional_on && <div className="text-[11px] text-amber-600">if {it.conditional_on.field}</div>}
              </td>
              <td className="py-3 text-xs text-slate-600">{it.verification_required ? "Required" : "—"}</td>
              <td className="py-3">{it.active
                ? <span className="text-xs font-semibold text-emerald-600">Active</span>
                : <span className="text-xs font-semibold text-slate-400">Archived</span>}</td>
              <td className="py-3 text-right whitespace-nowrap">
                <button onClick={() => onEdit(it)} className="p-1.5 text-slate-400 hover:text-vs-royal" data-testid={`tmpl-edit-${it.key}`}><Pencil className="h-4 w-4" /></button>
                {it.active
                  ? <button onClick={() => onArchive(it)} className="p-1.5 text-slate-400 hover:text-amber-600" data-testid={`tmpl-archive-${it.key}`}><Archive className="h-4 w-4" /></button>
                  : <button onClick={() => onRestore(it)} className="p-1.5 text-slate-400 hover:text-emerald-600" data-testid={`tmpl-restore-${it.key}`}><ArchiveRestore className="h-4 w-4" /></button>}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function RequirementTemplates() {
  const [ptype, setPtype] = React.useState("client");
  const [tmpl, setTmpl] = React.useState(null);
  const [history, setHistory] = React.useState(null);
  const [showHistory, setShowHistory] = React.useState(false);
  const [preview, setPreview] = React.useState(false);
  const [dialog, setDialog] = React.useState(null); // { kind, item|null }
  const [saving, setSaving] = React.useState(false);

  const load = React.useCallback(() => {
    setTmpl(null);
    api.get(`/admin/onboarding/templates/${ptype}`).then((r) => setTmpl(r.data)).catch((e) => { setTmpl(false); toast.error(apiError(e.response?.data?.detail)); });
  }, [ptype]);
  React.useEffect(() => { load(); setHistory(null); setShowHistory(false); }, [load]);

  const mutate = async (fn) => {
    setSaving(true);
    try { await fn(); toast.success("Template updated — new version saved"); load(); setDialog(null); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const onSave = (payload) => {
    const base = `/admin/onboarding/templates/${ptype}/${dialog.kind === "document" ? "document-requirements" : "profile-fields"}`;
    return mutate(() => dialog.item ? api.patch(`${base}/${dialog.item.key}`, payload) : api.post(base, payload));
  };
  const onArchive = (it) => mutate(() => api.delete(`/admin/onboarding/templates/${ptype}/${it.match_doc_types ? "document-requirements" : "profile-fields"}/${it.key}`));
  const onRestore = (it) => mutate(() => api.post(`/admin/onboarding/templates/${ptype}/${it.match_doc_types ? "document-requirements" : "profile-fields"}/${it.key}/restore`));

  const loadHistory = () => {
    if (history) { setShowHistory(!showHistory); return; }
    api.get(`/admin/onboarding/templates/${ptype}/history`).then((r) => { setHistory(r.data.items); setShowHistory(true); }).catch(() => toast.error("Unable to load history"));
  };

  return (
    <div>
      <PageHeader title="Partner Onboarding & Verification" description="Requirement templates drive mandatory profile fields, documents, completion percentages and verification gates for each partner type." breadcrumb="Administration / Partner Onboarding" />
      <div className="flex items-center gap-2 mb-5 flex-wrap" data-testid="tmpl-type-tabs">
        {TYPES.map((t) => (
          <button key={t.key} onClick={() => setPtype(t.key)} data-testid={`tmpl-tab-${t.key}`}
            className={`rounded-lg border px-4 py-2 text-sm font-semibold transition-colors ${ptype === t.key ? "border-vs-royal bg-vs-light text-vs-royal" : "border-slate-200 bg-white text-slate-500"}`}>
            {t.label}
          </button>
        ))}
        {tmpl && <span className="ml-auto text-xs text-slate-400" data-testid="tmpl-version">Template v{tmpl.version} · updated {new Date(tmpl.updated_at).toLocaleDateString()}</span>}
      </div>

      {tmpl === null ? <Card><SkeletonRows /></Card> : tmpl === false ? <Card><p className="text-sm text-rose-600">Unable to load template.</p></Card> : (
        <div className="space-y-6">
          <Card>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-vs-royal" />Profile Field Requirements <span className="text-xs font-normal text-slate-400">({tmpl.profile_fields.filter((f) => f.active).length} active)</span></h3>
              <div className="flex gap-2 flex-wrap">
                <Button variant="outline" size="sm" onClick={() => setPreview(true)} data-testid="tmpl-preview-btn"><Eye className="h-3.5 w-3.5 mr-1" />Preview</Button>
                <Button variant="outline" size="sm" onClick={loadHistory} data-testid="tmpl-history-btn"><History className="h-3.5 w-3.5 mr-1" />History{showHistory ? <ChevronUp className="h-3 w-3 ml-1" /> : <ChevronDown className="h-3 w-3 ml-1" />}</Button>
                <Button size="sm" className="bg-vs-royal text-white" onClick={() => setDialog({ kind: "profile", item: null })} data-testid="tmpl-add-field"><Plus className="h-3.5 w-3.5 mr-1" />Add Field</Button>
              </div>
            </div>
            <ItemsTable items={tmpl.profile_fields} kind="profile" ptype={ptype}
              onEdit={(it) => setDialog({ kind: "profile", item: it })} onArchive={onArchive} onRestore={onRestore} />
          </Card>

          <Card>
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2"><FileText className="h-4 w-4 text-vs-royal" />Document Requirements <span className="text-xs font-normal text-slate-400">({tmpl.document_requirements.filter((d) => d.active).length} active)</span></h3>
              <Button size="sm" className="bg-vs-royal text-white" onClick={() => setDialog({ kind: "document", item: null })} data-testid="tmpl-add-doc"><Plus className="h-3.5 w-3.5 mr-1" />Add Document</Button>
            </div>
            <ItemsTable items={tmpl.document_requirements} kind="document" ptype={ptype}
              onEdit={(it) => setDialog({ kind: "document", item: it })} onArchive={onArchive} onRestore={onRestore} />
            <p className="text-[11px] text-slate-400 mt-3">GST Certificate is optional by default and becomes required only when the organization declares GST registration. Changes are versioned and audited; archiving never deletes data.</p>
          </Card>

          {showHistory && history && (
            <Card title="Version History">
              {history.length === 0 ? <p className="text-sm text-slate-400">No changes yet — this is the seeded baseline.</p> : (
                <div className="space-y-3" data-testid="tmpl-history-list">
                  {history.map((h) => (
                    <div key={h.id} className="border-l-2 border-vs-royal/30 pl-3">
                      <div className="text-xs text-slate-400">v{h.version} · {h.changed_by} · {new Date(h.changed_at).toLocaleString()}</div>
                      {h.changes.map((c, i) => (
                        <div key={i} className="text-sm text-slate-600">{c.action} <code className="text-xs bg-slate-50 px-1 rounded">{c.key}{c.field ? `.${c.field}` : ""}</code>
                          {c.field ? <span className="text-slate-400"> {JSON.stringify(c.prev)} → {JSON.stringify(c.new)}</span> : null}</div>
                      ))}
                    </div>
                  ))}
                </div>
              )}
            </Card>
          )}
        </div>
      )}

      <Dialog open={preview} onOpenChange={setPreview}>
        <DialogContent className="max-w-md max-h-[80vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Partner Preview — {TYPES.find((t) => t.key === ptype)?.label}</DialogTitle>
            <DialogDescription>How the active requirement template appears to a registering partner.</DialogDescription></DialogHeader>
          {tmpl && (
            <div className="space-y-4 mt-2" data-testid="tmpl-preview">
              {[...new Set(tmpl.profile_fields.filter((f) => f.active).map((f) => f.section))].map((sec) => (
                <div key={sec}>
                  <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">{sec}</div>
                  {tmpl.profile_fields.filter((f) => f.active && f.section === sec).map((f) => (
                    <div key={f.key} className="text-sm text-slate-700 py-0.5">{f.label}
                      {f.required ? <span className="text-rose-500 ml-1">* Required</span> : <span className="text-slate-300 ml-1">Optional</span>}
                      {f.conditional_on && <span className="text-amber-500 text-xs ml-1">(if {f.conditional_on.field})</span>}
                    </div>
                  ))}
                </div>
              ))}
              <div>
                <div className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-1">Documents</div>
                {tmpl.document_requirements.filter((d) => d.active).map((d) => (
                  <div key={d.key} className="text-sm text-slate-700 py-0.5">{d.label}
                    {d.required ? <span className="text-rose-500 ml-1">REQUIRED</span> : <span className="text-slate-300 ml-1">Optional</span>}
                    {d.conditional_on && <span className="text-amber-500 text-xs ml-1">(if {d.conditional_on.field})</span>}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {dialog && <ItemDialog open={!!dialog} onClose={() => setDialog(null)} onSave={onSave} item={dialog.item} kind={dialog.kind} saving={saving} />}
    </div>
  );
}
